import React, {
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react'
import {
    ComposedChart,
    Line,
    YAxis,
    XAxis,
    Tooltip,
    Label,
    CartesianGrid,
    ResponsiveContainer,
    ReferenceArea,
    Legend,
} from 'recharts'

import { DataContext } from './DataContext'
import Message from '../../../elem/chart/Message'
import Spinner from '../../../elem/Spinner'
import ChartTooltip from '../../../elem/chart/Tooltip'
import { dateToString } from '../../../../utils/dateUtils'
import { AppStateContext } from '../AppStateContext'
import withConfig from '../../../wrappers/withConfig'
import dayjs from 'dayjs'
import palette from '../../../../utils/chart/palette'
import { getAxisYDomain } from '../../../../utils/chart/slice'
import { roundToFourDigits } from '../../../../utils/chart/values'
import CustomLegend from './chart/CustomLegend'

const getMeasureTypeDisplayValue = (measureCode, measureList) => measureList.find(x => x.MeasureType === measureCode).MeasureTypeDisplay

export default withConfig(({ config }) => {
    const {
        pressureChartDataSet,
        selectedMeasures,
        selectedMeasureUnits,
        zoomTrigger,
        measureList,
        selectedGrouping,
        isLoadingChart,
        visibleChartTab,
    } = useContext(DataContext)

    const dateField = 'MeasureDate'
    const keyField = 'FacilityID'
    const [data, setData] = useState([])

    const [left, setLeft] = useState('dataMin')
    const [right, setRight] = useState('dataMax')
    const [refAreaLeft, setRefAreaLeft] = useState('')
    const [refAreaRight, setRefAreaRight] = useState('')
    const [top, setTop] = useState('dataMax')
    const [bottom, setBottom] = useState('dataMin')

    const zoom = useCallback(() => {
        let RAL = refAreaLeft
        let RAR = refAreaRight
        if (RAL === RAR || RAR === '') {
            RAL = ''
            RAL = ''
            return
        }

        // xAxis domain
        if (RAL > RAR) {
            const temp = RAL
            RAL = RAR
            RAR = temp
        }

        // yAxis domain
        const leftAxisDomain = getAxisYDomain(RAL, RAR, (x) => !x.includes(dateField), 0, data, dateField)
        setBottom(leftAxisDomain[0])
        setTop(leftAxisDomain[1])

        setLeft(RAL)
        setRight(RAR)
        setRefAreaRight('')
        setRefAreaLeft('')
    }, [refAreaLeft, refAreaRight, data])

    const zoomOut = useCallback(() => {
        setRefAreaLeft('')
        setRefAreaRight('')
        setLeft('dataMin')
        setRight('dataMax')
        setTop('dataMax')
        setBottom('dataMin')
    }, [data])

    useEffect(() => {
        zoomOut()
    }, [zoomTrigger])

    const uniqueSeries = useMemo(
        () =>
            [
                ...new Set(
                    pressureChartDataSet
                        .map(x => x[keyField])
                ),
            ]
                .sort()
                .map((x, idx) => ({
                    name: x,
                    color: palette[idx % palette.length],
                })),
        [pressureChartDataSet]
    )

    const seriesData = useMemo(() => {
        const grouping = selectedGrouping && selectedGrouping.Code
        const measureFilteredChartData = pressureChartDataSet
            .filter(d => {
                if (grouping) {
                    return !!selectedMeasures.find(x => x.MeasureType === d.MeasureType && x.MeasureUnit === d.MeasureUnit) && d.Grouping === grouping 
                }
                return !!selectedMeasures.find(x => x.MeasureType === d.MeasureType && x.MeasureUnit === d.MeasureUnit) 
            })
        if (measureFilteredChartData && measureFilteredChartData.length) {
            const dates = [
                ...new Set(
                    measureFilteredChartData.map(x => x[dateField])
                ),
            ]
            const d = dates.reduce((x, c) => {
                const entry = uniqueSeries.reduce((acc, curr) => {
                    const matchDate = pressureChartDataSet.find(w => w[dateField] === c && w[keyField] === curr.name)
                    if (matchDate) {
                        return {
                            ...acc,
                            [curr.name]: matchDate['Measurement']

                        }
                    } else {
                        return acc
                    }
                }, {[dateField]: c})

                return [
                    ...x,
                    entry
                ]
            }, [])
            return d
        }
        return measureFilteredChartData
    }, [pressureChartDataSet, uniqueSeries, selectedMeasures, selectedGrouping])

    useEffect(() => {
        if (seriesData && seriesData.length) {
            const d = seriesData.map(x => ({
                ...x,
                [dateField]: dayjs(x[dateField])
                    .toDate()
                    .getTime(),
            }))
            setData(d)
        } else {
            setData([])
        }
    }, [seriesData])

    const legendData = useMemo(
        () =>
            uniqueSeries &&
            uniqueSeries.map(({ name, color }) => ({
                color,
                type: 'line',
                dataKey: name,
                value: name,
            })),
        [uniqueSeries]
    )

    // if loading || no data, return nothing
    if (visibleChartTab !== 'pressure') {
        return null
    }
    if (isLoadingChart) {
        return <Spinner extraClass="chartWrapper" />
    }
    if (!data.length
        || data.every(x => !x.MeasureDate)) {
        return <Message text={`No Chart Data Available`} />
    }

    const measureNameDisplay = selectedMeasures && selectedMeasures[0] && selectedMeasures[0].MeasureTypeDisplay
    const measureUnitDisplay = 
        selectedMeasureUnits && selectedMeasureUnits.MeasureUnitDisplay ? selectedMeasureUnits.MeasureUnitDisplay : 'units unknown'
    
    // construct chart
    const yAxisLabel = `Measurement (${measureUnitDisplay})`
    const xAxisLabel = 'Measurement Date'

    // shared chart props
    const animationDuration = 200
    const lines = uniqueSeries.map(({ name, color }, idx) => (
        <Line
            key={`time-series-${name}`}
            type="monotone"
            dataKey={name}
            name={name}
            unit={measureUnitDisplay}
            stroke={color}
            connectNulls 
            strokeWidth={1}
            dot={{ r: 1, fill: color, stroke: color }}
            activeDot={{ r: 5, fill: color, stroke: color }}
            animationDuration={animationDuration}
            isAnimationActive={false} 
        />
    ))
    const pressureChartTitle = `${measureNameDisplay}`
    
    return (
        <div className="chart">
            <div className="chartTitle">{pressureChartTitle}</div>
            <div className="chartWrapper">
                <ResponsiveContainer width="100%" minHeight={'250px'}>
                    <ComposedChart
                        data={
                            data
                                .sort((a, b) => a.SampleDate - b.SampleDate)
                                .slice()
                        }
                        margin={{
                            top: 10,
                            right: 50,
                            left: 12,
                            bottom: 15,
                        }}
                        onMouseDown={e => e && e.activeLabel && setRefAreaLeft(e.activeLabel)}
                        onMouseMove={e => e && e.activeLabel && refAreaLeft && setRefAreaRight(e.activeLabel)}
                        // eslint-disable-next-line react/jsx-no-bind
                        onMouseUp={() => zoom()}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            allowDataOverflow
                            type="number"
                            dataKey={dateField}
                            domain={[left, right]}
                            tickFormatter={unixTime => dateToString(unixTime)}
                        >
                            <Label
                                value={xAxisLabel}
                                offset={-10}
                                position="insideBottom"
                                className="nitrateChartXAxisLabel"
                            />
                        </XAxis>
                        <YAxis 
                            interval='preserveStartEnd'
                            // allowDataOverflow 
                            type="number" 
                            domain={[bottom, top]} 
                            tickFormatter={v => roundToFourDigits(v)} 
                            yAxisId={0}
                        >
                            <Label
                                value={yAxisLabel}
                                angle={-90}
                                offset={20}
                                position="insideLeft"
                                className="compareChartYAxisLabel"
                            />
                        </YAxis>
                        <Tooltip content={<ChartTooltip stagger={90}/>} allowEscapeViewBox={{x: true, y: true}}/>
                        <Legend
                            content={
                                <CustomLegend legendPayload={legendData} />
                            }
                            verticalAlign="bottom"
                            wrapperStyle={{
                                bottom: `0`,
                                left: 0,
                                alignItems: 'center',
                                width: '100%',
                            }}
                        />
                        {lines}
                        {/* {dots} */}
                        {refAreaLeft && refAreaRight ? (
                            <ReferenceArea
                                yAxisId={0}
                                x1={refAreaLeft}
                                x2={refAreaRight}
                                strokeOpacity={0.3}
                            />
                        ) : null}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
})